import type { StoreApi } from "zustand";

import type { ConsolidatedLensItem } from "../../../../services/lensService";
import { getLensContent, getLensData } from "../../../../services/lensService";
import { logError } from "../../../../utils/error";
import { Lens, LensGroup } from "../../types";
import { extractDescriptionFromMarkdown } from "../../utils/markdown";
import type { AccountAnalysisStore } from "../accountAnalysisStore";

export interface LensActions {
  // Lens Groups
  setLensGroups: (groups: LensGroup[]) => void;
  setLenses: (lenses: Lens[]) => void;
  setIsLoadingLensGroups: (loading: boolean) => void;
  setLensGroupsError: (error: string | null) => void;
  // Selection state
  setSelectedLensGroupName: (groupName: string | null) => void;
  setSelectedLensPath: (path: string | null) => void;
  setCurrentLensContent: (content: string) => void;
  // Async actions
  fetchLensGroups: (tenantId: string) => Promise<void>;
  fetchLensContent: (tenantId: string, fullPath: string) => Promise<void>;
  selectLensGroup: (groupName: string) => void;
  selectLens: (tenantId: string, fullPath: string) => Promise<void>;
}

export const createLensSlice = (
  set: StoreApi<AccountAnalysisStore>["setState"],
  get: StoreApi<AccountAnalysisStore>["getState"]
): LensActions => ({

  // Sync actions
  setLensGroups: (lensGroups) => set({ lensGroups }),
  setLenses: (lenses) => set({ lenses }),
  setIsLoadingLensGroups: (isLoadingLensGroups) => set({ isLoadingLensGroups }),
  setLensGroupsError: (lensGroupsError) => set({ lensGroupsError }),
  setSelectedLensGroupName: (selectedLensGroupName) => set({ selectedLensGroupName }),
  setSelectedLensPath: (selectedLensPath) => set({ selectedLensPath }),
  setCurrentLensContent: (currentLensContent) => set({ currentLensContent }),

  // Async actions
  fetchLensGroups: async (tenantId: string) => {
    set({ isLoadingLensGroups: true, lensGroupsError: null });
    try {
      // Use a dummy lens name since we only need the consolidated list
      const lensData = await getLensData(tenantId, "dummy");

      const lensGroups: LensGroup[] = [];
      const lenses: Lens[] = [];
      const groupFiles: Map<string, string[]> = new Map();

      // Process consolidated lens list to build groups and lenses with descriptions
      const lensPromises = lensData.consolidatedLensList.map(async (item: ConsolidatedLensItem) => {
        let description: string | null = null;

        try {
          const content = await getLensContent(tenantId, item.fullPath);
          description = extractDescriptionFromMarkdown(content);
        } catch (error) {
          console.warn(`Failed to fetch content for lens ${item.fullPath}:`, error);
        }

        return {
          id: `lens-${item.category}-${item.filename}`,
          groupName: item.category,
          name: item.filename.replace(".md", ""),
          filename: item.filename,
          fullPath: item.fullPath,
          description
        };
      });

      // Wait for all lens descriptions to be extracted
      const lensesWithDescriptions = await Promise.all(lensPromises);
      lenses.push(...lensesWithDescriptions);

      // Track files per group
      lensData.consolidatedLensList.forEach((item: ConsolidatedLensItem) => {
        if (!groupFiles.has(item.category)) {
          groupFiles.set(item.category, []);
        }
        groupFiles.get(item.category)!.push(item.filename);
      });

      // Build lens groups from unique categories
      groupFiles.forEach((files, groupName) => {
        const isIndustry = lensData.industry === groupName;
        const description = isIndustry && lensData.protocolIndex
          ? lensData.protocolIndex.description
          : null;

        lensGroups.push({
          id: `group-${groupName}`,
          name: groupName,
          description,
          lensFiles: files,
          source: isIndustry ? "industry" : "custom"
        });
      });

      set({
        lensGroups,
        lenses,
        isLoadingLensGroups: false
      });
    } catch (error) {
      set({
        lensGroupsError: error instanceof Error ? error.message : "Failed to fetch lens group data",
        isLoadingLensGroups: false
      });
    }
  },

  fetchLensContent: async (tenantId: string, fullPath: string) => {
    try {
      const content = await getLensContent(tenantId, fullPath);
      set({ currentLensContent: content });
    } catch (error) {
      logError("Failed to fetch lens content", error);
      set({ currentLensContent: "" });
    }
  },

  selectLensGroup: (groupName: string) => {
    set({
      selectedLensGroupName: groupName,
      selectedLensPath: null,
      currentLensContent: ""
    });
  },

  selectLens: async (tenantId: string, fullPath: string) => {
    set({ selectedLensPath: fullPath });
    // Fetch content for the selected lens
    const actions = get() as LensActions;
    await actions.fetchLensContent(tenantId, fullPath);
  }
});
